import React, { useMemo, useState } from "react";
import {
  Button,
  DropdownField,
  MultiSelectField,
  PinnedActionBar,
  TextField,
} from "../../../components/ui";
import {
  digitsOf,
  normaliseEmailInput,
  normalisePhoneInput,
  toNameCase,
} from "../../../lib/fieldFormat";
import { ALL_CITIES } from "../../../lib/locations";
import KeyboardSpacer from "../../shared/components/KeyboardSpacer";
// Shared with the pre-consultation form, which asks the same question the
// same way — see `GenderMarks`.
import { GENDER_CHOICES } from "../../shared/components/GenderMarks";
import { usePublicContactCheck } from "../hooks/usePublicContactCheck";
import {
  EMPTY_ANSWERS,
  normaliseNameInput,
  validate,
} from "../publicEnquiryValidation";
import type {
  PublicEnquiryField,
  PublicEnquiryErrors,
} from "../publicEnquiryValidation";
import type {
  PublicEnquiryAnswers,
  PublicEnquiryForm,
} from "../types/enquiries.types";

/**
 * The questions themselves — nine of them, against the fifteen the
 * dashboard's own Add enquiry form asks.
 *
 * The difference is deliberate and is the whole design of this screen: it is
 * opened by a stranger on a phone who has not decided yet whether to get in
 * touch. It asks for what a practitioner needs in order to reply and leaves
 * the rest — a date of birth, a profession, a postal address — for the
 * conversation that follows.
 *
 * Grouped into three cards because that is what makes nine fields feel like
 * three short questions: who you are, what you want, and how to reach you.
 */

/** The order the fields are laid out in, which is the order a failing one is
 *  looked for in when Submit is pressed. */
const FIELD_ORDER: PublicEnquiryField[] = [
  "firstName",
  "lastName",
  "age",
  "gender",
  "goals",
  "lifestyle",
  "email",
  "phone",
  "city",
];

const controlId = (field: PublicEnquiryField) => `enquiry-${field}`;

/** One white card. The spacing between them is what separates the questions. */
const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="rounded-xl border border-line bg-white p-5 sm:p-4">
    {children}
  </section>
);


/**
 * The two gender choices, as a pair of buttons rather than a dropdown.
 *
 * Two options are not a list worth opening: the pair costs one tap where a
 * dropdown costs two, and both answers are visible without anything being
 * dismissed — which matters more on a phone than anywhere else.
 */
const GenderChoice: React.FC<{
  value: string;
  onChange: (value: string) => void;
  error?: string;
}> = ({ value, onChange, error }) => (
  <fieldset>
    <legend className="mb-1.5 text-[13px] 3xl:text-[14px] text-ink">
      I am
    </legend>
    <div className="grid grid-cols-2 gap-3 sm:max-w-[380px]">
      {GENDER_CHOICES.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            id={option.value === "Male" ? controlId("gender") : undefined}
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className={
              // `rounded-[6px]` is the shared controls' radius, not a number
              // chosen here: these buttons stand in a row with a text field
              // and under two more, and a corner rounded harder than the box
              // beside it reads as a different kind of control.
              "flex items-center justify-center gap-2.5 rounded-[6px] border " +
              // The pair has a row of its own on a phone, so the horizontal
              // padding is the comfortable one rather than the one that fits.
              // The vertical is the shared shell's, so a button stands exactly
              // as tall as the field it is in a row with from `sm:` up.
              "px-3 py-2.5 text-[14px] transition-colors 3xl:px-4 3xl:py-3 " +
              (selected
                ? "border-brand bg-brand-soft text-ink"
                : "border-line bg-white text-ink hover:border-ink-subtle")
            }
          >
            <span className="text-brand">{option.icon}</span>
            {option.value}
          </button>
        );
      })}
    </div>
    {error && (
      <p role="alert" className="mt-1.5 text-[12px] text-red-500">
        {error}
      </p>
    )}
  </fieldset>
);

interface PublicEnquiryFieldsProps {
  /**
   * Whose form this is — the practitioner's own name, read off the subdomain
   * or the path. Needed here and not only by the page above because the
   * contact check is asked field by field as they are typed, not once at the
   * end. See `usePublicContactCheck`.
   */
  handle: string;
  /** The vocabulary this practitioner's form offers. */
  form: PublicEnquiryForm;
  /** The send is with the server — the button says so and refuses a second. */
  submitting: boolean;
  /** What the server said, where it refused the whole submission. */
  submitError?: string;
  /**
   * What the server said about one answer in particular — an email address it
   * already holds an enquiry from, a number it has seen before. Shown under
   * the control it belongs to rather than in the message above the button: a
   * duplicate is a problem with one field, and saying so at the top of an
   * nine-field form leaves the reader hunting for which.
   */
  fieldErrors?: Partial<Record<PublicEnquiryField, string>>;
  /** Drops one of those the moment its field is edited — see `set` below. */
  onClearFieldError: (field: PublicEnquiryField) => void;
  onSubmit: (answers: PublicEnquiryAnswers) => void;
}

const PublicEnquiryFields: React.FC<PublicEnquiryFieldsProps> = ({
  handle,
  form,
  submitting,
  submitError,
  fieldErrors,
  onClearFieldError,
  onSubmit,
}) => {
  const [answers, setAnswers] = useState<PublicEnquiryAnswers>(EMPTY_ANSWERS);
  // A field's error only surfaces once it has been visited, so the form does
  // not open covered in red before anything has been typed.
  const [touched, setTouched] = useState<
    Partial<Record<PublicEnquiryField, boolean>>
  >({});

  const errors: PublicEnquiryErrors = useMemo(
    () => validate(answers),
    [answers]
  );

  // Asked while the form is filled in, so somebody who already wrote in hears
  // it under the field rather than after pressing Send. Empty whenever the
  // check has not run, found nothing, or could not be made.
  const duplicates = usePublicContactCheck(handle, answers.email, answers.phone);

  /**
   * Every reason the form will not go, in one map: its own rules, and the two
   * contacts the practitioner already holds.
   *
   * A duplicate belongs here rather than beside the errors because it stops
   * the submit for the same reason a malformed address does — the server would
   * refuse it — and the button reads this to know whether there is anything to
   * send.
   */
  const problems: PublicEnquiryErrors = useMemo(
    () => ({ ...errors, ...duplicates }),
    [errors, duplicates]
  );
  const valid = Object.keys(problems).length === 0;

  const set = (patch: Partial<PublicEnquiryAnswers>) => {
    setAnswers((current) => ({ ...current, ...patch }));
    // A verdict the server gave about the old answer says nothing about the
    // new one. Editing the field is what withdraws it — leaving it up would
    // report a duplicate against an address that has just been changed.
    (Object.keys(patch) as PublicEnquiryField[]).forEach(onClearFieldError);
  };

  const visit = (field: PublicEnquiryField) => () =>
    setTouched((current) => ({ ...current, [field]: true }));

  /**
   * What is shown under one control.
   *
   * The server's word comes first and shows whether or not the field has been
   * visited: it is a verdict on an answer already sent, so waiting for a blur
   * that may never come would hide it. The form's own rules only surface once
   * their field has been touched, so the page does not open covered in red.
   */
  const errorFor = (field: PublicEnquiryField) =>
    fieldErrors?.[field] ??
    // Shown whether or not the field has been visited, for the same reason the
    // server's word above it is: it is a verdict on what was actually typed,
    // and the check only ran because the value was well-formed and finished.
    duplicates[field] ??
    (touched[field] ? errors[field] : undefined);

  const goalNames = useMemo(
    () => form.goals.map((goal) => goal.name),
    [form.goals]
  );
  const lifeStyleNames = useMemo(
    () => form.lifeStyles.map((style) => style.name),
    [form.lifeStyles]
  );

  const submit = () => {
    if (submitting) return;

    if (!valid) {
      // Mark everything visited so every problem shows at once, then take the
      // reader to the first one — on a phone the failing field is very often
      // above or below the fold, and reddening it silently says nothing.
      setTouched(
        Object.fromEntries(FIELD_ORDER.map((field) => [field, true]))
      );
      const first = FIELD_ORDER.find((field) => problems[field]);
      if (first) {
        const control = document.getElementById(controlId(first));
        control?.scrollIntoView({ behavior: "smooth", block: "center" });
        control?.focus({ preventScroll: true });
      }
      return;
    }

    onSubmit(answers);
  };

  return (
    <div>
      <h1 className="text-[26px] sm:text-[28px] font-semibold tracking-[-0.01em] text-[#0A4A52] sm:text-center">
        Fill your details
      </h1>

      <form
        className="mt-6 sm:mt-6"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
        noValidate
      >
        {/* The cards carry the rhythm; the bar below is outside it, so its own
            margin is free to pull it up over the last one. */}
        <div className="space-y-4 sm:space-y-3">
        <Card>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-4">
            <TextField
              id={controlId("firstName")}
              label="First name"
              placeholder="Riya"
              value={answers.firstName}
              onValueChange={(value) =>
                set({ firstName: toNameCase(normaliseNameInput(value)) })
              }
              onBlur={visit("firstName")}
              error={errorFor("firstName")}
              autoComplete="given-name"
            />
            <TextField
              id={controlId("lastName")}
              label="Last name"
              placeholder="Roy"
              value={answers.lastName}
              onValueChange={(value) =>
                set({ lastName: toNameCase(normaliseNameInput(value)) })
              }
              onBlur={visit("lastName")}
              error={errorFor("lastName")}
              autoComplete="family-name"
            />
          </div>

          {/* A row each on a phone, side by side from `sm:` up.

              They used to share a row at every width, on the reading that two
              answers of a moment — two digits and a tap — did not deserve two
              rows. Sharing 320px between them is what that costs: an age box
              barely wider than its own placeholder, and two gender buttons
              squeezed until "Female" is nearly touching its edges. Neither
              reads as a comfortable target, which is the only thing a phone
              layout has to get right.

              So below `sm` each gets a row of its own, and both span it: the
              age box the full width, the gender pair two halves worth
              pressing. A box wider than the answer it takes is the cheaper
              mistake here — the card is a column of full-width controls, and
              one short field in the middle of it breaks that line for the sake
              of three digits.

              `items-start` so a rejected age reddens under its own box without
              lifting the buttons beside it, on the widths where they share a
              row. */}
          <div className="mt-4 grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            <TextField
              id={controlId("age")}
              label="Age"
              placeholder="eg 45"
              value={answers.age}
              onValueChange={(value) => set({ age: digitsOf(value).slice(0, 3) })}
              onBlur={visit("age")}
              error={errorFor("age")}
              // The number pad, and no spinner: a stepper on a phone is a
              // target nobody hits and a way of nudging an age by one on a
              // scroll. Three digits is the most an age can need.
              inputMode="numeric"
            />
            <GenderChoice
              value={answers.gender}
              onChange={(gender) => {
                set({ gender });
                visit("gender")();
              }}
              error={errorFor("gender")}
            />
          </div>
        </Card>

        <Card>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-4">
            {/* Several at once, the way the dashboard's own Add and Edit
                enquiry screens ask it: somebody writing in about their weight
                and their energy is describing one enquiry, and a control that
                takes a single answer makes them drop half of what they came to
                say. At least one is still required. */}
            <MultiSelectField
              id={controlId("goals")}
              label="Goal"
              placeholder="Weight loss"
              values={answers.goals}
              options={goalNames}
              onValuesChange={(goals) => set({ goals })}
              onBlur={visit("goals")}
              error={errorFor("goals")}
            />
            <DropdownField
              id={controlId("lifestyle")}
              label="Life style"
              placeholder="Semi active"
              value={answers.lifestyle}
              options={lifeStyleNames}
              onValueChange={(lifestyle) => set({ lifestyle })}
              onBlur={visit("lifestyle")}
              error={errorFor("lifestyle")}
            />
          </div>
        </Card>

        <Card>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-4">
            <TextField
              id={controlId("email")}
              label="Email address"
              placeholder="yourname@gmail.com"
              value={answers.email}
              onValueChange={(value) => set({ email: normaliseEmailInput(value) })}
              onBlur={visit("email")}
              error={errorFor("email")}
              type="email"
              inputMode="email"
              autoComplete="email"
            />
            <TextField
              id={controlId("phone")}
              label="Phone number"
              placeholder="7869 369 854"
              prefix="+91"
              value={answers.phone}
              onValueChange={(value) => set({ phone: normalisePhoneInput(value) })}
              onBlur={visit("phone")}
              error={errorFor("phone")}
              inputMode="numeric"
              autoComplete="tel-national"
            />
            <DropdownField
              id={controlId("city")}
              label="City"
              placeholder="eg. Mumbai"
              value={answers.city}
              options={ALL_CITIES}
              onValueChange={(city) => set({ city })}
              onBlur={visit("city")}
              error={errorFor("city")}
              // Twelve hundred of them: the filter box is the only way through
              // a list that long, and the control caps what it draws itself.
              searchable
              // And the list is not the last word. It is one dataset of Indian
              // towns, so somebody in a place it has never heard of — or
              // spelling one differently — can type theirs and pick `Use "…"`
              // rather than being asked to claim they live somewhere else. A
              // form a stranger abandons over a missing town is worth far more
              // than a tidy city column.
              creatable
            />
          </div>
        </Card>

        {submitError && (
          <p
            role="alert"
            className="rounded-lg bg-red-50 px-4 py-3 text-[13px] leading-6 text-red-600"
          >
            {submitError}
          </p>
        )}
        </div>

        {/* Keeps the bar below sliding all the way to the bottom edge while a
            keyboard is up, instead of stopping over the field being typed
            into — see `KeyboardSpacer`. Nothing at all otherwise. */}
        <KeyboardSpacer />

        {/*
          One button, laid out two ways — not two buttons with one hidden.
          A `hidden` class removes something from the picture but not from the
          page: both would still be in the accessibility tree, and a screen
          reader would offer to submit the form twice.

          On a phone the bar is `sticky`, not `fixed`. It looks the same while
          you scroll — pinned to the bottom, the action always one thumb away —
          but it stays part of the page, so nothing has to be reserved
          underneath it. A fixed bar floats over the form, which is why this
          used to need a spacer at the end to keep the last field reachable, and
          that spacer is what opened an empty band above the bar once you
          scrolled to the bottom.

          The background is `#FFFFFF26` — white at 15% — rather than solid, so
          the field under it stays half visible and the bar reads as glass laid
          over the form rather than as a panel cutting the end off it.

          Two numbers make that effect visible rather than merely present. The
          blur is 8px — enough to soften the field behind without dissolving
          it; past about 16px the text stops reading as text at all. And the
          bottom padding is 2rem rather than 1rem, so a strip of glass shows
          *under* the button as well as behind it — that strip is where the
          blurred field beneath is actually seen, since the button itself covers
          everything else.

          `mt-4` keeps the last card clear of it, so at the foot of the scroll
          the card ends, the page shows for a moment, and then the bar starts —
          the separation the design draws between the two.

          There is no rule along the top. The glass separates itself from the
          form well enough — a tint and a blur are already a change of surface —
          and a hairline over it only draws a second edge where the card below
          has just drawn one.

          `-mx-5` cancels the shell's phone gutter so the bar still runs edge to
          edge; from `sm:` up it drops out of the flow entirely — no tint, no
          blur — and sits centred under the last card, where there is nothing to
          lay glass over. Below `sm:` the glass is `PinnedActionBar`'s rather
          than this one's: the same bar the dashboard's forms end in, blurring
          what passes under without laying a fill over it. The tint written
          here is what is left for the band between `sm:` and the point the bar
          goes static, and the fallback for a browser that cannot blur.

          The 1.5rem under it there is the column's, drawn here rather than by the
          shell: the foot of these pages belongs to the bar at both widths now, so
          that the pre-consultation form's — which is held against the bottom edge
          on a desktop window too — has nothing showing beneath it. See `VERTICAL`.
        */}
        <PinnedActionBar
          className={
            "-mx-5 mt-4 sm:mt-2 " +
            "bg-[#FFFFFF26] backdrop-blur " +
            "px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4 " +
            "sm:static sm:z-auto sm:mx-0 sm:flex sm:justify-center " +
            "sm:bg-transparent sm:backdrop-blur-none sm:px-0 sm:pb-6 sm:pt-3"
          }
        >
          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={submitting}
            disabled={!valid || submitting}
            // Pressed anyway rather than dead: a disabled button dispatches
            // nothing, so the press that asks "why can I not send this" would
            // never land. This routes it to the answer instead.
            onDisabledClick={submit}
            className="sm:w-auto sm:min-w-[220px]"
          >
            Submit
          </Button>
        </PinnedActionBar>
      </form>
    </div>
  );
};

export default PublicEnquiryFields;
